/**
 * Workflow Entity Configuration
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * - Which entities are available for each trigger event
 * - What fields are exposed on each entity for workflow conditions
 * - Field types and operators for type-safe condition building
 *
 * Based on backend models:
 * - Customer: /v2-backend/app/models/customer.py
 * - Event (Appointment): /v2-backend/app/models/event.py
 * - Invoice: /v2-backend/app/models/invoice.py
 */

/**
 * Maps trigger events to available entities
 * When a workflow is triggered by an event, these entities are instantiated
 */
export const TRIGGER_EVENT_ENTITIES = {
  // Customer events
  'customer_created': ['customer'],
  'customer_updated': ['customer'],
  'customer_field_updated': ['customer', 'field_change'],  // NEW: Custom field changed

  // Document events
  'document_uploaded': ['customer', 'document'],  // NEW: Document attached to customer
  'document_removed': ['customer', 'document'],   // NEW: Document removed from customer

  // Appointment events
  'appointment_created': ['customer', 'appointment'],
  'appointment_completed': ['customer', 'appointment'],
  'appointment_cancelled': ['customer', 'appointment'],

  // Invoice events
  'invoice_sent': ['customer', 'appointment', 'invoice'],
  'invoice_paid': ['customer', 'appointment', 'invoice'],
  'invoice_overdue': ['customer', 'appointment', 'invoice'],

  // Manual approval events
  'manual_approval_completed': ['customer', 'approval']  // NEW: User approved/rejected in workflow
};

/**
 * Field type definitions
 */
export const FIELD_TYPES = {
  STRING: 'string',
  EMAIL: 'email',
  PHONE: 'phone',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  DATE: 'date',
  DATETIME: 'datetime',
  ENUM: 'enum'
};

/**
 * Operator definitions by field type
 */
export const OPERATORS_BY_TYPE = {
  [FIELD_TYPES.STRING]: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' }
  ],
  [FIELD_TYPES.EMAIL]: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' }
  ],
  [FIELD_TYPES.PHONE]: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' }
  ],
  [FIELD_TYPES.NUMBER]: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'greater_than_or_equal', label: 'Greater Than or Equal' },
    { value: 'less_than_or_equal', label: 'Less Than or Equal' }
  ],
  [FIELD_TYPES.BOOLEAN]: [
    { value: 'equals', label: 'Is' }
  ],
  [FIELD_TYPES.DATE]: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'after', label: 'After' },
    { value: 'before', label: 'Before' },
    { value: 'between', label: 'Between' }
  ],
  [FIELD_TYPES.DATETIME]: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'after', label: 'After' },
    { value: 'before', label: 'Before' },
    { value: 'between', label: 'Between' }
  ],
  [FIELD_TYPES.ENUM]: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'in', label: 'Is One Of' }
  ]
};

/**
 * Entity field definitions
 * Each entity has fields with type, label, and optional enum values
 */
export const ENTITY_FIELDS = {
  customer: [
    { field: 'name', type: FIELD_TYPES.STRING, label: 'Customer Name' },
    { field: 'email', type: FIELD_TYPES.EMAIL, label: 'Email Address' },
    { field: 'phone', type: FIELD_TYPES.PHONE, label: 'Phone Number' },
    { field: 'how_heard', type: FIELD_TYPES.STRING, label: 'How They Heard About Us' },
    { field: 'sms_consent', type: FIELD_TYPES.BOOLEAN, label: 'SMS Consent' },
    { field: 'created_at', type: FIELD_TYPES.DATETIME, label: 'Customer Created Date' },
    { field: 'updated_at', type: FIELD_TYPES.DATETIME, label: 'Customer Updated Date' }
  ],

  appointment: [
    { field: 'title', type: FIELD_TYPES.STRING, label: 'Appointment Title' },
    { field: 'scheduled_datetime', type: FIELD_TYPES.DATETIME, label: 'Scheduled Date/Time' },
    { field: 'duration_minutes', type: FIELD_TYPES.NUMBER, label: 'Duration (Minutes)' },
    { field: 'description', type: FIELD_TYPES.STRING, label: 'Description' },
    {
      field: 'status',
      type: FIELD_TYPES.ENUM,
      label: 'Status',
      enumValues: [
        { value: 'pending', label: 'Pending' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
      ]
    },
    {
      field: 'event_category',
      type: FIELD_TYPES.ENUM,
      label: 'Category',
      enumValues: [
        { value: 'service', label: 'Service' },
        { value: 'general', label: 'General' }
      ]
    },
    { field: 'service_type', type: FIELD_TYPES.STRING, label: 'Service Type' },
    { field: 'service_address', type: FIELD_TYPES.STRING, label: 'Service Address' },
    { field: 'is_blocking', type: FIELD_TYPES.BOOLEAN, label: 'Is Blocking Calendar' },
    { field: 'sms_consent', type: FIELD_TYPES.BOOLEAN, label: 'SMS Consent for Event' }
  ],

  invoice: [
    { field: 'invoice_number', type: FIELD_TYPES.STRING, label: 'Invoice Number' },
    {
      field: 'status',
      type: FIELD_TYPES.ENUM,
      label: 'Status',
      enumValues: [
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'paid', label: 'Paid' },
        { value: 'partially_paid', label: 'Partially Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'cancelled', label: 'Cancelled' }
      ]
    },
    { field: 'invoice_date', type: FIELD_TYPES.DATE, label: 'Invoice Date' },
    { field: 'due_date', type: FIELD_TYPES.DATE, label: 'Due Date' },
    { field: 'sent_date', type: FIELD_TYPES.DATETIME, label: 'Sent Date' },
    { field: 'paid_date', type: FIELD_TYPES.DATETIME, label: 'Paid Date' },
    { field: 'subtotal', type: FIELD_TYPES.NUMBER, label: 'Subtotal' },
    { field: 'tax_amount', type: FIELD_TYPES.NUMBER, label: 'Tax Amount' },
    { field: 'total_amount', type: FIELD_TYPES.NUMBER, label: 'Total Amount' },
    { field: 'paid_amount', type: FIELD_TYPES.NUMBER, label: 'Paid Amount' },
    { field: 'notes', type: FIELD_TYPES.STRING, label: 'Notes' }
  ],

  // NEW: Field change entity - available when customer_field_updated triggers
  field_change: [
    { field: 'field_key', type: FIELD_TYPES.STRING, label: 'Field Key' },
    { field: 'old_value', type: FIELD_TYPES.STRING, label: 'Previous Value' },
    { field: 'new_value', type: FIELD_TYPES.STRING, label: 'New Value' }
  ],

  // NEW: Document entity - available when document_uploaded/document_removed triggers
  document: [
    { field: 'document_type', type: FIELD_TYPES.STRING, label: 'Document Type' },
    { field: 'file_name', type: FIELD_TYPES.STRING, label: 'File Name' },
    { field: 'mime_type', type: FIELD_TYPES.STRING, label: 'MIME Type' }
  ],

  // NEW: Approval entity - available when manual_approval_completed triggers
  approval: [
    {
      field: 'decision',
      type: FIELD_TYPES.ENUM,
      label: 'Decision',
      enumValues: [
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }
      ]
    },
    { field: 'approver_name', type: FIELD_TYPES.STRING, label: 'Approver Name' },
    { field: 'notes', type: FIELD_TYPES.STRING, label: 'Approval Notes' },
    { field: 'approved_at', type: FIELD_TYPES.DATETIME, label: 'Approval Time' }
  ]
};

/**
 * Get available entities for a workflow based on entry node trigger
 *
 * @param {Array} nodes - React Flow nodes
 * @param {string} currentNodeId - Current node ID (for future extensions)
 * @returns {Array<string>} Array of entity names available in this workflow
 */
export function getAvailableEntities(nodes, currentNodeId = null) {
  const entryNode = nodes?.find(n => n.type === 'entry' || n.data?.isEntry);
  if (!entryNode) return ['customer']; // Default fallback

  const triggerEvent = entryNode.data?.triggerEvent;
  if (!triggerEvent) return ['customer'];

  return TRIGGER_EVENT_ENTITIES[triggerEvent] || ['customer'];
}

/**
 * Get all fields for a specific entity type
 *
 * @param {string} entityType - Entity name ('customer', 'appointment', 'invoice')
 * @returns {Array<Object>} Array of field definitions
 */
export function getEntityFields(entityType) {
  return ENTITY_FIELDS[entityType] || [];
}

/**
 * Get field definition by entity and field name
 *
 * @param {string} entityType - Entity name
 * @param {string} fieldName - Field name
 * @returns {Object|null} Field definition or null if not found
 */
export function getFieldDefinition(entityType, fieldName) {
  const fields = getEntityFields(entityType);
  return fields.find(f => f.field === fieldName) || null;
}

/**
 * Get available operators for a field type
 *
 * @param {string} fieldType - Field type from FIELD_TYPES
 * @returns {Array<Object>} Array of operator definitions
 */
export function getOperatorsForFieldType(fieldType) {
  return OPERATORS_BY_TYPE[fieldType] || OPERATORS_BY_TYPE[FIELD_TYPES.STRING];
}

/**
 * Get available operators for a specific field
 *
 * @param {string} entityType - Entity name
 * @param {string} fieldName - Field name
 * @returns {Array<Object>} Array of operator definitions
 */
export function getOperatorsForField(entityType, fieldName) {
  const fieldDef = getFieldDefinition(entityType, fieldName);
  if (!fieldDef) return OPERATORS_BY_TYPE[FIELD_TYPES.STRING];

  return getOperatorsForFieldType(fieldDef.type);
}

/**
 * Parse a condition field string into entity and field name
 *
 * @param {string} fieldPath - Field path like "customer.email"
 * @returns {Object} Object with entityType and fieldName
 */
export function parseFieldPath(fieldPath) {
  if (!fieldPath || typeof fieldPath !== 'string') {
    return { entityType: null, fieldName: null };
  }

  const parts = fieldPath.split('.');
  if (parts.length !== 2) {
    return { entityType: null, fieldName: null };
  }

  return {
    entityType: parts[0],
    fieldName: parts[1]
  };
}

/**
 * Build a field path from entity and field name
 *
 * @param {string} entityType - Entity name
 * @param {string} fieldName - Field name
 * @returns {string} Field path like "customer.email"
 */
export function buildFieldPath(entityType, fieldName) {
  return `${entityType}.${fieldName}`;
}

/**
 * Validate a condition against available entities
 *
 * @param {Object} condition - Condition object with field, operator, value
 * @param {Array<string>} availableEntities - Available entity names
 * @returns {Object} Validation result with valid flag and error message
 */
export function validateCondition(condition, availableEntities) {
  const { field, operator, value } = condition;

  if (!field || !operator) {
    return { valid: false, error: 'Missing field or operator' };
  }

  const { entityType, fieldName } = parseFieldPath(field);

  if (!entityType || !fieldName) {
    return { valid: false, error: 'Invalid field format. Expected format: entity.field' };
  }

  if (!availableEntities.includes(entityType)) {
    return {
      valid: false,
      error: `Entity '${entityType}' not available in this workflow`
    };
  }

  const fieldDef = getFieldDefinition(entityType, fieldName);
  if (!fieldDef) {
    return {
      valid: false,
      error: `Field '${fieldName}' not found on ${entityType}`
    };
  }

  const validOperators = getOperatorsForFieldType(fieldDef.type);
  const isValidOperator = validOperators.some(op => op.value === operator);
  if (!isValidOperator) {
    return {
      valid: false,
      error: `Operator '${operator}' not valid for ${fieldDef.type} field`
    };
  }

  // Value validation could be added here

  return { valid: true };
}
